import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { DateRangeType } from "react-tailwindcss-datepicker";

import PartsManagementTable from "../../components/dashboard/PartsManagementTable";
import StationCycleTimeTable from "../../components/dashboard/StationCycleTimeTable";
import VehicleDismantlingListsTable from "../../components/dashboard/VehicleDismantlingListsTable";
import VehicleProcessingTable from "../../components/dashboard/VehicleProcessingTable";
import OverviewKpiCard from "../../components/productionDashboard/OverviewKpiCard";
import PartsUserActivityTable from "../../components/shared/Performance/PartsUserActivityTable";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useDashboardStats from "../../data/useDashboardStats";
import useStationCycleTimes from "../../data/useStationCycleTimes";
import { IDashboardStats } from "../../models/DashboardStats";
import PriceFormat from "../../utils/priceFormat";
import toIsoDate from "../../utils/toIsoDate";

type RangeKey = "today" | "week" | "month";

/**
 * The management dashboard, which TME see simply as "Dashboard".
 *
 * Deliberately not the stock Dashboard. That one is built around buying vehicles from the public —
 * lead acquisition, seller payments, CoD chasing, commission — none of which TME do. This shows
 * what an ELV recycler runs on: what is in the yard, what is queueing, what came off the line and
 * how long each station is taking.
 *
 * The figures come from the same /stats/range pipeline the stock dashboard uses, so nothing is
 * calculated a second way here. Cycle times are the one exception and have their own endpoint,
 * because they need a range the user picks rather than the fixed tabs.
 */
export default function TmeDashboard() {
  const { t } = useTranslation("dashboard");
  const { setInfo } = usePageNameContext();
  const { todayStats, weekToDateStats, monthToDateStats } = useDashboardStats();

  const [range, setRange] = useState<RangeKey>("today");

  // Cycle times default to the last 30 days rather than today: a single day is often too few
  // vehicles per station for a median to mean anything.
  const [cycleDates, setCycleDates] = useState<DateRangeType>({
    startDate: DateTime.now().minus({ days: 29 }).toJSDate(),
    endDate: DateTime.now().toJSDate(),
  });

  const cycleFrom = useMemo(() => toIsoDate(cycleDates.startDate), [cycleDates.startDate]);
  const cycleTo = useMemo(() => toIsoDate(cycleDates.endDate), [cycleDates.endDate]);
  const { cycleTimes } = useStationCycleTimes(cycleFrom, cycleTo);

  useEffect(() => {
    setInfo({
      name: t("Dashboard"),
      desc: t("Yard inventory, processing queues and line performance"),
    });
  }, [setInfo, t]);

  const statsByRange: Record<RangeKey, IDashboardStats | undefined> = useMemo(
    () => ({
      today: todayStats.data?.data,
      week: weekToDateStats.data?.data,
      month: monthToDateStats.data?.data,
    }),
    [todayStats.data, weekToDateStats.data, monthToDateStats.data]
  );

  const rangeTabs: { key: RangeKey; label: string }[] = [
    { key: "today", label: t("Today") },
    { key: "week", label: t("Week") },
    { key: "month", label: t("Month") },
  ];

  const selected = statsByRange[range];

  // Only block on first load. Switching tabs reads an already-fetched query, and the stats hook
  // fires all three in parallel on mount.
  const isFirstLoad = todayStats.isLoading && !todayStats.data;

  if (isFirstLoad) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingWheel />
      </div>
    );
  }

  const numberValue = (value: number | null | undefined) => (value == null ? "—" : String(value));

  return (
    <div className="mt-2 flex flex-col gap-4">
      {/* ---- Range tabs. Inventory tiles ignore these deliberately; see below. ---- */}
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex rounded-md bg-gray-100 p-0.5 text-xs font-medium">
          {rangeTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setRange(tab.key)}
              className={
                range === tab.key
                  ? "rounded bg-gray-900 px-2.5 py-1 text-white"
                  : "rounded px-2.5 py-1 text-gray-700"
              }
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ---- ELV inventory. A snapshot of the yard, so it does not follow the tabs: there is no
             history of Vehicle.Status to look back through. Labelled so that is obvious. ---- */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <OverviewKpiCard
          label={t("ELVs On Site")}
          value={numberValue(selected?.totalVehiclesOnsite)}
          footer={t("In the yard now")}
        />
        <OverviewKpiCard
          label={t("Awaiting Dismantling")}
          value={numberValue(selected?.vehiclesAwaitingDismantling)}
          subValue={
            selected?.vehiclesDismantlingOngoing != null
              ? t("{{count}} dismantling ongoing", { count: selected.vehiclesDismantlingOngoing })
              : undefined
          }
          footer={t("Queue depth now")}
        />
        <OverviewKpiCard
          label={t("Parts In Stock")}
          value={numberValue(
            (selected?.listedStockCount ?? 0) + (selected?.unlistedStockCount ?? 0)
          )}
          subValue={PriceFormat(
            (selected?.listedStockValue ?? 0) + (selected?.unlistedStockValue ?? 0)
          )}
          footer={t("{{listed}} listed, {{unlisted}} unlisted", {
            listed: selected?.listedStockCount ?? 0,
            unlisted: selected?.unlistedStockCount ?? 0,
          })}
        />
        <OverviewKpiCard
          label={t("ELVs Dismantled")}
          value={numberValue(selected?.vehicleDismantledToday)}
          footer={rangeTabs.find((tab) => tab.key === range)?.label}
        />
      </div>

      {/* ---- Today's throughput. These do follow the selected range. ---- */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <OverviewKpiCard
          label={t("Vehicles Checked In")}
          value={numberValue(selected?.vehiclesCheckedInToday)}
        />
        <OverviewKpiCard
          label={t("Parts Removed")}
          value={numberValue(selected?.partsRemovedToday)}
        />
        <OverviewKpiCard
          label={t("Parts Sold")}
          value={numberValue(selected?.partsSoldTotal)}
          subValue={PriceFormat(selected?.partsSoldTotalValue ?? 0)}
        />
        <OverviewKpiCard
          label={t("Engines Removed")}
          value={numberValue(selected?.exportEnginesRemovedToday)}
        />
      </div>

      {/* ---- Cycle time by station, on its own range ---- */}
      {/* isLoading && isFetching, not isLoading alone: a disabled query (cleared date range) stays
          at isLoading forever in react-query v4, and a background refetch would otherwise replace
          a populated table with a spinner. */}
      <StationCycleTimeTable
        stations={cycleTimes.data?.data?.stations}
        isLoading={cycleTimes.isLoading && cycleTimes.isFetching}
        dates={cycleDates}
        onDatesChange={setCycleDates}
        span={2}
      />

      {/* ---- Queues ---- */}
      {/* <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <VehicleProcessingTable dashboardStats={selected} span={1} />
        <VehicleDismantlingListsTable dashboardStats={selected} span={1} />
      </div> */}

      {/* ---- Parts ---- */}
      {/* <div className="grid grid-cols-1 gap-4">
        <PartsManagementTable dashboardStats={selected} span={2} />
      </div> */}

      {/* ---- Who did what. Same component as the stock dashboard and Performance Tracking. ---- */}
      {/* {statsByRange.today && statsByRange.week && statsByRange.month && (
        <PartsUserActivityTable
          todayStats={statsByRange.today}
          weekStats={statsByRange.week}
          monthStats={statsByRange.month}
          title={t("Personnel Activity")}
          rowLabelColumn={t("User")}
        />
      )} */}
    </div>
  );
}
